// AmendmentCreateForm — F-9. Identity tuple is read-only display
// (server copies); per api-design §3 :amend, presence in body returns
// 422 immutable_field, so we never send those fields.

import { useEffect, useMemo } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  agreementsApi,
  companiesApi,
  peopleApi,
  type AmendmentCreateBody,
} from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { useToast } from '@/components/ToastHost';
import { mapApiErrorToFormErrors } from '@/utils/formErrors';
import { useDirtyFormReloadGuard } from '@/utils/useDirtyFormReloadGuard';
import type { Agreement, AgreementType, Principal } from '@/types/domain';
import { isPL, isOwnerOf } from '@/auth/permissions';

interface Props {
  source: Agreement;
  principal: Principal | null;
}

interface FormShape {
  type: AgreementType;
  start_date: string;
  end_date: string;
  total_value: string;
  estimated_value: string;
  description: string;
  owner_person_id: string;
}

const KNOWN: string[] = [
  'type',
  'start_date',
  'end_date',
  'total_value',
  'estimated_value',
  'description',
  'owner_person_id',
];

export function AmendmentCreateForm({ source, principal }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const owners = useQuery({
    queryKey: qk.people({ role: ['admin', 'practice_lead'] }),
    queryFn: () => peopleApi.list({ role: ['admin', 'practice_lead'], limit: 100 }),
    staleTime: 60_000,
  });
  const clientQ = useQuery({
    queryKey: qk.companies({ all: true }),
    queryFn: () => companiesApi.list({ limit: 100 }),
    staleTime: 5 * 60_000,
  });
  const clientName = clientQ.data?.data.find((c) => c.id === source.client_id)?.name;

  const defaultOwner = useMemo(() => {
    if (!principal) return '';
    if (isPL(principal) && isOwnerOf(principal, source))
      return source.owner_person_id;
    return '';
  }, [principal, source]);

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting, isDirty },
    setValue,
  } = useForm<FormShape>({
    defaultValues: {
      type: source.type,
      start_date: '',
      end_date: '',
      total_value: '',
      estimated_value: '',
      description: '',
      owner_person_id: defaultOwner,
    },
  });

  useEffect(() => {
    if (defaultOwner && !watch('owner_person_id')) {
      setValue('owner_person_id', defaultOwner);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOwner]);

  const type = watch('type');
  const isFP = type === 'fixed_price';
  const isTM = type === 't_and_m';

  const m = useMutation({
    mutationFn: (body: AmendmentCreateBody) =>
      agreementsApi.amend(source.id, body),
  });

  const onSubmit: SubmitHandler<FormShape> = async (values) => {
    try {
      const body: AmendmentCreateBody = {
        type: values.type,
        start_date: values.start_date,
        end_date: values.end_date ? values.end_date : null,
        total_value: isFP && values.total_value ? values.total_value : null,
        estimated_value:
          isTM && values.estimated_value ? values.estimated_value : null,
        description: values.description ? values.description : null,
        owner_person_id: values.owner_person_id,
      };
      const created = await m.mutateAsync(body);
      void qc.invalidateQueries({ queryKey: ['agreements'] });
      toast.show({ severity: 'success', message: 'Amendment created.' });
      navigate(`/agreements/${created.id}`);
    } catch (err) {
      const mapped = mapApiErrorToFormErrors(err, KNOWN);
      for (const [field, message] of Object.entries(mapped.fieldErrors)) {
        setError(field as keyof FormShape, { type: 'server', message });
      }
      if (mapped.formMessage)
        toast.show({ severity: 'error', message: mapped.formMessage });
    }
  };

  // Browser warning for dirty form (V1 safety net).
  useDirtyFormReloadGuard(isDirty);

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e);
      }}
      noValidate
    >
      <section className="section">
        <h2>Identity (carried from source agreement)</h2>
        <dl className="field-list">
          <dt>Client</dt>
          <dd>{clientName ?? <span className="muted">Loading…</span>}</dd>
          <dt>Project name</dt>
          <dd>{source.project_name}</dd>
          <dt>SOW ID</dt>
          <dd>{source.sow_id}</dd>
          <dt>Source amendment</dt>
          <dd>{source.amendment}</dd>
        </dl>
        <p className="muted">
          The new amendment number is computed by the server.
        </p>
      </section>

      <fieldset className="form-field">
        <legend>
          Type<span className="form-required">*</span>
        </legend>
        <label style={{ marginRight: 16 }}>
          <input
            type="radio"
            value="t_and_m"
            disabled={isSubmitting}
            {...register('type', { required: 'Choose a type.' })}
          />{' '}
          T&M
        </label>
        <label>
          <input
            type="radio"
            value="fixed_price"
            disabled={isSubmitting}
            {...register('type', { required: 'Choose a type.' })}
          />{' '}
          Fixed-Price
        </label>
        {errors.type ? (
          <span className="form-error">{errors.type.message}</span>
        ) : null}
      </fieldset>

      <div className="form-field">
        <label htmlFor="am-start">
          Start date<span className="form-required">*</span>
        </label>
        <input
          id="am-start"
          type="date"
          disabled={isSubmitting}
          {...register('start_date', { required: 'Start date is required.' })}
        />
        {errors.start_date ? (
          <span className="form-error">{errors.start_date.message}</span>
        ) : null}
      </div>
      <div className="form-field">
        <label htmlFor="am-end">End date</label>
        <input
          id="am-end"
          type="date"
          disabled={isSubmitting}
          {...register('end_date', {
            validate: (v) => {
              if (!v) return true;
              const start = watch('start_date');
              if (start && v < start)
                return 'End date must be on or after the start date.';
              return true;
            },
          })}
        />
        {errors.end_date ? (
          <span className="form-error">{errors.end_date.message}</span>
        ) : null}
      </div>
      {isFP ? (
        <div className="form-field">
          <label htmlFor="am-tv">
            Total value (USD)<span className="form-required">*</span>
          </label>
          <input
            id="am-tv"
            inputMode="decimal"
            disabled={isSubmitting}
            {...register('total_value', {
              required: isFP ? 'Total value is required for Fixed-Price.' : false,
              pattern: {
                value: /^\d+(?:\.\d{1,2})?$/,
                message: 'Enter dollars and cents.',
              },
            })}
          />
          {errors.total_value ? (
            <span className="form-error">{errors.total_value.message}</span>
          ) : null}
        </div>
      ) : null}
      {isTM ? (
        <div className="form-field">
          <label htmlFor="am-ev">Estimated value (USD)</label>
          <input
            id="am-ev"
            inputMode="decimal"
            disabled={isSubmitting}
            {...register('estimated_value', {
              pattern: {
                value: /^\d+(?:\.\d{1,2})?$/,
                message: 'Enter dollars and cents.',
              },
            })}
          />
        </div>
      ) : null}
      <div className="form-field">
        <label htmlFor="am-desc">Description</label>
        <textarea
          id="am-desc"
          rows={4}
          disabled={isSubmitting}
          {...register('description')}
        />
      </div>
      <div className="form-field">
        <label htmlFor="am-owner">
          Owner<span className="form-required">*</span>
        </label>
        <select
          id="am-owner"
          disabled={isSubmitting || owners.isPending}
          {...register('owner_person_id', { required: 'Choose an owner.' })}
        >
          <option value="">Select an owner…</option>
          {owners.data?.data.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.email})
            </option>
          ))}
        </select>
        {errors.owner_person_id ? (
          <span className="form-error">{errors.owner_person_id.message}</span>
        ) : null}
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(-1)}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
          aria-busy={isSubmitting ? 'true' : 'false'}
        >
          {isSubmitting ? 'Creating…' : 'Create amendment'}
        </button>
      </div>
    </form>
  );
}
